import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  IconArrowLeft, IconSunHigh, IconTag, IconSend, IconMoodHappy,
  IconAlertHexagon, IconPill, IconTrophy, IconCalendar, IconWand,
  IconCheck, IconShield, IconLock, IconPlus, IconTrash, IconFirstAidKit,
  IconMailForward, IconRefresh, IconEdit,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useChild, initials, TAG_STYLES, relativeTime } from "@/pages/app/circulo/hooks";

const TAGS = ["sueño", "emoción", "evento", "medicación", "logro"];
const TAG_ICONS = {
  "sueño": <IconMoodHappy size={13} />,
  "emoción": <IconAlertHexagon size={13} />,
  "evento": <IconCalendar size={13} />,
  "medicación": <IconPill size={13} />,
  "logro": <IconTrophy size={13} />,
};

/**
 * Expediente shared view.
 * Props: childId (from URL), viewMode ("familia" | "docente" | "admin")
 */
export default function Expediente({ viewMode }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const { childId } = useParams();
  const { child, loading, reload } = useChild(childId);
  const [tab, setTab] = useState("home");

  if (loading) {
    return (
      <div className="wrap-app py-10 text-tert font-mono text-[11px] tracking-widest uppercase">
        Cargando expediente...
      </div>
    );
  }
  if (!child) {
    return (
      <div className="wrap-app py-10">
        <p>{lang === "es" ? "No se encontró el expediente." : "Case not found."}</p>
        <button className="btn mt-4" onClick={() => nav("/app/circulo")}>{t("app.back")}</button>
      </div>
    );
  }

  const canManageAccess = viewMode === "familia" || viewMode === "admin";
  const isReadOnlyPro = viewMode === "docente";

  const tabs = [
    { id: "home", label_es: "Inicio", label_en: "Home" },
    { id: "bitacora", label_es: "Bitácora", label_en: "Log" },
    { id: "reporte", label_es: "Reporte", label_en: "Report" },
    ...(canManageAccess ? [{ id: "accesos", label_es: "Accesos", label_en: "Access" }] : []),
    { id: "kit", label_es: "Botiquín", label_en: "Calm kit" },
  ];

  const subLabel = {
    familia: "CÍRCULO · FAMILIA",
    docente: "CÍRCULO · DOCENTE",
    admin: "EXPEDIENTE",
  }[viewMode] || "CÍRCULO";

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app/circulo")} data-testid="expediente-back">
          <IconArrowLeft size={13} /> {t("app.back")}
        </button>
        <span className="t">{subLabel}</span>
      </div>
      <div className="wrap-app max-w-[860px] py-10">
        <ChildHeader child={child} />

        <div className="border-b border-line mt-8 flex gap-1 overflow-x-auto">
          {tabs.map((tt) => (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              className={`px-3 py-2 text-[13px] font-medium transition border-b-2 -mb-px ${
                tab === tt.id ? "border-teal text-teal-deep" : "border-transparent text-muted hover:text-teal-deep"
              }`}
              data-testid={`expediente-tab-${tt.id}`}
            >
              {lang === "es" ? tt.label_es : tt.label_en}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-6"
          >
            {tab === "home" && <TabHome child={child} viewMode={viewMode} onChange={reload} />}
            {tab === "bitacora" && <TabBitacora child={child} viewMode={viewMode} />}
            {tab === "reporte" && <TabReporte child={child} viewMode={viewMode} />}
            {tab === "accesos" && canManageAccess && <TabAccesos child={child} viewMode={viewMode} />}
            {tab === "kit" && <TabKit child={child} viewMode={viewMode} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </>
  );
}

function ChildHeader({ child }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-14 h-14 rounded-full bg-teal-deep text-white flex items-center justify-center font-display text-[19px] font-medium">
        {initials(child.name)}
      </div>
      <div className="flex-1">
        <p className="font-display text-[22px] font-medium leading-tight">{child.name}</p>
        <p className="text-tert text-[13px]">
          {child.age} años · {child.grade || "grado no especificado"} · {child.city || child.country}
        </p>
      </div>
      {child.condition && (
        <span className="tag tag-admin">{child.condition}</span>
      )}
    </div>
  );
}

// ---------- Tab: HOME (parte del día + quick registro) ----------
function TabHome({ child, viewMode, onChange }) {
  const { t, lang } = useI18n();
  const [text, setText] = useState("");
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [recent, setRecent] = useState([]);
  const [ok, setOk] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/circulo/children/${child.id}/bitacora?limit=5`);
      setRecent(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [child.id]);

  const toggleTag = (tag) => setTags((cur) => cur.includes(tag) ? cur.filter(x => x !== tag) : [...cur, tag]);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true); setError(""); setOk(false);
    try {
      await api.post(`/circulo/children/${child.id}/bitacora`, { text: text.trim(), tags });
      setText(""); setTags([]); setOk(true);
      await load();
      if (onChange) onChange();
      setTimeout(() => setOk(false), 3000);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const today = recent[0];

  return (
    <div className="space-y-5">
      {today && (
        <div className="card border-l-[3px] border-l-honey">
          <div className="flex items-center gap-2 text-teal-deep font-medium text-[13px]">
            <IconSunHigh size={16} /> {lang === "es" ? "Parte del día" : "Daily note"}
          </div>
          <p className="text-[14.5px] mt-2 leading-relaxed">{today.text}</p>
          <div className="flex gap-2 mt-3">
            {today.tags.map((tg) => (
              <span key={tg} className={`tag ${TAG_STYLES[tg] || ""}`}>{tg}</span>
            ))}
          </div>
        </div>
      )}

      {/* Any seat-holder can write (backend enforces via child_access). Includes
          parents, invited "profesor del niño", sombra and admin/psicóloga. */}
      <div className="card">
        <p className="lbl m-0 mb-2">
          {lang === "es" ? `¿Qué pasó hoy con ${child.name.split(" ")[0]}?` : `What happened today?`}
        </p>
          <textarea
            className="textarea"
            placeholder={lang === "es" ? "Escribe lo que la escuela debería saber..." : "Write what the school should know..."}
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="bitacora-text"
          />
          <div className="flex flex-wrap gap-2 mt-3">
            {TAGS.map((tg) => (
              <button
                key={tg}
                className={`chip ${tags.includes(tg) ? "sel" : ""}`}
                onClick={() => toggleTag(tg)}
                data-testid={`bitacora-tag-${tg}`}
              >
                {TAG_ICONS[tg]} {tg}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button className="btn" onClick={submit} disabled={saving || !text.trim()} data-testid="bitacora-submit">
              <IconSend size={13} /> {saving ? "..." : (lang === "es" ? "Registrar y avisar" : "Register & notify")}
            </button>
            {ok && <span className="text-oktext text-[12px] flex items-center gap-1"><IconCheck size={13} /> {lang === "es" ? "Guardado" : "Saved"}</span>}
          </div>
          {error && <p className="text-errtext text-[13px] mt-2">{error}</p>}
        </div>

      <div>
        <p className="lbl">{lang === "es" ? "Últimos registros" : "Latest entries"}</p>
        {recent.length === 0 ? (
          <p className="text-tert text-[13px]">{lang === "es" ? "Sin registros aún." : "No entries yet."}</p>
        ) : (
          <div className="space-y-2">
            {recent.map((r) => <EntryRow key={r.id} entry={r} />)}
          </div>
        )}
      </div>
    </div>
  );
}

const ROLE_LABEL_TAG_STYLE = {
  profesor: "bg-teal-soft text-teal-deep",
  docente: "bg-teal-soft text-teal-deep",
  sombra: "bg-honey-soft text-honey-deep",
  familiar: "bg-warnbg text-warntext",
  psicologa: "bg-teal text-white",
  admin: "bg-teal text-white",
  madre: "bg-warnbg text-warntext",
  padre: "bg-warnbg text-warntext",
  tutor: "bg-warnbg text-warntext",
};

function CircleRoleTag({ role, label }) {
  if (!role) return null;
  const style = ROLE_LABEL_TAG_STYLE[role] || "bg-line text-muted";
  return (
    <span className={`tag text-[10.5px] ${style}`} data-testid={`role-tag-${role}`}>
      {label || role}
    </span>
  );
}

function EntryRow({ entry }) {
  const { lang } = useI18n();
  const roleLabel = entry.author_circle_role_label_es || entry.author_role || "";
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium">{entry.author_name}</span>
          <CircleRoleTag role={entry.author_circle_role || entry.author_role} label={roleLabel} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11.5px] text-tert font-mono">{relativeTime(entry.created_at, lang)}</span>
          {entry.tags.map((tg) => (
            <span key={tg} className={`tag ${TAG_STYLES[tg] || ""}`}>{tg}</span>
          ))}
        </div>
      </div>
      <p className="text-[14px] leading-relaxed">{entry.text}</p>
    </div>
  );
}

// ---------- Tab: BITACORA ----------
function TabBitacora({ child }) {
  const { lang } = useI18n();
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [text, setText] = useState("");
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await api.get(`/circulo/children/${child.id}/bitacora${filter && filter !== "all" ? `?tag=${filter}` : ""}`);
    setEntries(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, child.id]);

  const toggleTag = (tag) => setTags((cur) => cur.includes(tag) ? cur.filter(x => x !== tag) : [...cur, tag]);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true); setError("");
    try {
      await api.post(`/circulo/children/${child.id}/bitacora`, { text: text.trim(), tags });
      setText(""); setTags([]);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {/* Composer — cualquier miembro del círculo puede iniciar/continuar la conversación */}
      <div className="card mb-4" data-testid="bit-composer">
        <p className="lbl m-0 mb-2">
          {lang === "es" ? "Nueva entrada en la bitácora" : "New bitácora entry"}
        </p>
        <textarea
          className="textarea"
          placeholder={lang === "es" ? "Escribí un mensaje, pregunta o registro para el círculo..." : "Write a message, question or note for the circle..."}
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="bit-text"
        />
        <div className="flex flex-wrap gap-2 mt-3">
          {TAGS.map((tg) => (
            <button
              key={tg}
              className={`chip ${tags.includes(tg) ? "sel" : ""}`}
              onClick={() => toggleTag(tg)}
              data-testid={`bit-tag-${tg}`}
            >
              {TAG_ICONS[tg]} {tg}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button className="btn" onClick={submit} disabled={saving || !text.trim()} data-testid="bit-submit">
            <IconSend size={13} /> {saving ? "..." : (lang === "es" ? "Publicar" : "Post")}
          </button>
          <span className="text-tert text-[11.5px]">
            {lang === "es"
              ? "Cualquiera del círculo (padres, profesor(a) del niño, sombra, Mariela) puede iniciar y responder."
              : "Anyone in the circle can start or reply."}
          </span>
        </div>
        {error && <p className="text-errtext text-[13px] mt-2">{error}</p>}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button className={`chip ${filter === "all" ? "sel" : ""}`} onClick={() => setFilter("all")} data-testid="bit-filter-all">
          {lang === "es" ? "todas" : "all"}
        </button>
        {TAGS.map((tg) => (
          <button key={tg} className={`chip ${filter === tg ? "sel" : ""}`} onClick={() => setFilter(tg)} data-testid={`bit-filter-${tg}`}>
            {tg}
          </button>
        ))}
      </div>
      {entries.length === 0 ? (
        <p className="text-tert text-[13px]">{lang === "es" ? "Sin registros en este filtro." : "No entries."}</p>
      ) : (
        <div className="space-y-2">
          {entries.map((r) => <EntryRow key={r.id} entry={r} />)}
        </div>
      )}
    </div>
  );
}

// ---------- Tab: REPORTE ----------
function TabReporte({ child, viewMode }) {
  const { lang } = useI18n();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isAdmin = viewMode === "admin";
  const [editing, setEditing] = useState(null); // report_id being edited
  const [editText, setEditText] = useState("");

  const load = async () => {
    const { data } = await api.get(`/circulo/children/${child.id}/reports`);
    setReports(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [child.id]);

  const generate = async () => {
    setLoading(true); setError("");
    try {
      await api.post(`/circulo/children/${child.id}/report`, { lang });
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const approve = async (reportId) => {
    try {
      await api.patch(`/circulo/reports/${reportId}/approve`, { approve: true });
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/circulo/reports/${editing}/edit`, { content: editText });
      setEditing(null); setEditText("");
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex items-center gap-2 flex-wrap">
          <button className="btn" onClick={generate} disabled={loading} data-testid="report-generate">
            <IconWand size={13} /> {loading ? "..." : (lang === "es" ? "Generar reporte con Sistema Inteligente" : "Generate report with Smart System")}
          </button>
          <span className="text-tert text-[12px]">
            {lang === "es" ? "Sistema Inteligente · resumen de la última semana · exclusivo administración" : "Smart System · last week summary · admin only"}
          </span>
        </div>
      )}
      {error && <p className="text-errtext text-[13px]">{error}</p>}

      {reports.length === 0 ? (
        <p className="text-tert text-[13px]">{lang === "es" ? "Aún no hay reportes. Genera el primero." : "No reports yet."}</p>
      ) : (
        <div className="space-y-4">
          {reports.map((r) => (
            <div key={r.id} className="card" data-testid={`report-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[12px] font-mono text-tert">
                  {new Date(r.period_start).toLocaleDateString()} → {new Date(r.period_end).toLocaleDateString()} · {r.entries_count} {lang === "es" ? "registros" : "entries"}
                </div>
                <span className={`tag ${r.status === "sent" ? "bg-okbg text-oktext" : r.status === "approved" ? "bg-teal-soft text-teal-deep" : ""}`}>
                  {r.status === "sent" ? (lang === "es" ? `enviado (${r.recipients_count || 0})` : `sent (${r.recipients_count || 0})`) : r.status}
                </span>
              </div>
              {editing === r.id ? (
                <>
                  <textarea className="textarea" value={editText} onChange={(e) => setEditText(e.target.value)} rows="10" data-testid="report-edit-text" />
                  <div className="flex gap-2 mt-2">
                    <button className="btn" onClick={saveEdit} data-testid="report-save-edit"><IconCheck size={13} /> Guardar</button>
                    <button className="btn-ghost btn" onClick={() => { setEditing(null); setEditText(""); }}>Cancelar</button>
                  </div>
                </>
              ) : (
                <div className="whitespace-pre-wrap text-[14px] leading-relaxed">{r.content}</div>
              )}
              {isAdmin && editing !== r.id && (
                <div className="flex gap-2 mt-3">
                  {r.status === "pending" && (
                    <button className="btn" onClick={() => approve(r.id)} data-testid={`report-approve-${r.id}`}>
                      <IconCheck size={13} /> {lang === "es" ? "Aprobar" : "Approve"}
                    </button>
                  )}
                  <button className="btn-ghost btn" onClick={() => { setEditing(r.id); setEditText(r.content); }} data-testid={`report-edit-${r.id}`}>
                    <IconEdit size={13} /> {lang === "es" ? "Editar" : "Edit"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Tab: ACCESOS ----------
const ROLE_LABEL = {
  profesor: { es: "Profesor(a) del niño", en: "Child's teacher" },
  docente: { es: "Profesor(a) del niño", en: "Child's teacher" },  // legacy
  sombra: { es: "Maestra sombra", en: "Shadow teacher" },
  familiar: { es: "Familiar", en: "Family" },
  psicologa: { es: "Psicóloga", en: "Psychologist" },
};
function TabAccesos({ child }) {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState({ accepted: [], pending: [], expired: [], totals: { accepted: 0, pending: 0, expired: 0 } });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("profesor");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [resendingId, setResendingId] = useState(null);
  const [flash, setFlash] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    const [{ data: accessData }, { data: histData }] = await Promise.all([
      api.get(`/circulo/children/${child.id}/access`),
      api.get(`/circulo/children/${child.id}/access/history`),
    ]);
    setItems(accessData);
    setHistory(histData);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [child.id]);

  const invite = async () => {
    if (!name.trim()) return;
    setSaving(true); setError("");
    try {
      await api.post(`/circulo/children/${child.id}/access`, {
        name: name.trim(), email: email.trim() || null, role,
      });
      setName(""); setEmail("");
      setFlash(lang === "es" ? "Invitación creada. Se envió un correo con el enlace." : "Invitation created. Email sent.");
      setTimeout(() => setFlash(""), 4000);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/circulo/children/${child.id}/access/${id}`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const resend = async (a) => {
    if (!a.email) return;
    setResendingId(a.id); setError("");
    try {
      await api.post(`/circulo/children/${child.id}/access/${a.id}/resend`);
      setFlash(lang === "es" ? `Invitación reenviada a ${a.email}.` : `Invitation resent to ${a.email}.`);
      setTimeout(() => setFlash(""), 4000);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setResendingId(null); }
  };

  return (
    <div className="space-y-4">
      <p className="text-muted text-[13.5px]">
        {lang === "es"
          ? `Tú decides quién ve el expediente de ${child.name.split(" ")[0]}. El expediente no es público.`
          : "You decide who sees the case file. Private by default."}
      </p>

      <div className="space-y-2">
        {items.length === 0 && (
          <p className="text-tert text-[13px]">{lang === "es" ? "Aún no hay invitados." : "No invites yet."}</p>
        )}
        {items.map((a) => {
          const roleLabel = ROLE_LABEL[a.role]?.[lang] || a.role;
          const isPending = a.status === "pending";
          return (
            <div key={a.id} className="card flex items-center justify-between flex-wrap gap-2" data-testid={`access-item-${a.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-teal-soft text-teal-deep flex items-center justify-center font-medium text-[13px]">
                  {initials(a.name)}
                </div>
                <div>
                  <p className="text-[14px] font-medium">{a.name}</p>
                  <p className="text-tert text-[12px]">
                    {a.email || (lang === "es" ? "sin correo" : "no email")} · <span className="text-teal-deep">{roleLabel}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`tag ${a.status === "active" ? "bg-okbg text-oktext" : "bg-warnbg text-warntext"}`}>{a.status}</span>
                {isPending && a.email && (
                  <button
                    className="btn-ghost btn text-[11px] px-2 py-1"
                    onClick={() => resend(a)}
                    disabled={resendingId === a.id}
                    data-testid={`access-resend-${a.id}`}
                    title={lang === "es" ? "Reenviar invitación" : "Resend invitation"}
                  >
                    <IconMailForward size={12} /> {resendingId === a.id ? "…" : (lang === "es" ? "Reenviar" : "Resend")}
                  </button>
                )}
                <button className="btn-ghost btn text-[11px] px-2 py-1 text-errtext" onClick={() => revoke(a.id)} data-testid={`access-revoke-${a.id}`}>
                  <IconTrash size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {flash && <p className="text-oktext text-[13px] bg-okbg rounded-md px-3 py-2" data-testid="access-flash">{flash}</p>}

      <div className="card">
        <p className="lbl m-0 mb-3">{lang === "es" ? "Invitar a alguien" : "Invite someone"}</p>
        <div className="grid sm:grid-cols-4 gap-2">
          <input className="input" placeholder={lang === "es" ? "Nombre" : "Name"} value={name} onChange={(e) => setName(e.target.value)} data-testid="access-name" />
          <input className="input" type="email" placeholder="correo@ejemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="access-email" />
          <select className="select-css" value={role} onChange={(e) => setRole(e.target.value)} data-testid="access-role">
            <option value="profesor">{ROLE_LABEL.profesor[lang]}</option>
            <option value="sombra">{ROLE_LABEL.sombra[lang]}</option>
            <option value="familiar">{ROLE_LABEL.familiar[lang]}</option>
          </select>
          <button className="btn" onClick={invite} disabled={saving} data-testid="access-invite">
            <IconPlus size={13} /> {lang === "es" ? "Invitar" : "Invite"}
          </button>
        </div>
        <p className="text-tert text-[11.5px] mt-2">
          {lang === "es"
            ? "Le llegará un correo con enlace para aceptar; si no tiene cuenta, la crea desde ahí. Este asiento no otorga el módulo Docentes ni ningún otro; solo abre este expediente."
            : "They'll receive an email link to accept; if they don't have an account, they create one from there. This seat does NOT grant the Docentes module or any other; it only unlocks this file."}
        </p>
        {error && <p className="text-errtext text-[13px] mt-2">{error}</p>}
      </div>

      {/* HISTORIAL DE INVITACIONES */}
      <div className="card">
        <button
          type="button"
          className="w-full flex items-center justify-between text-left"
          onClick={() => setShowHistory((s) => !s)}
          data-testid="history-toggle"
        >
          <span className="lbl m-0">
            {lang === "es" ? "Historial de invitaciones" : "Invitation history"}
            <span className="text-tert font-mono text-[11px] ml-2">
              · {history.totals.accepted} aceptadas · {history.totals.pending} pendientes · {history.totals.expired} vencidas
            </span>
          </span>
          <span className="text-tert text-[11px]">{showHistory ? "▲" : "▼"}</span>
        </button>
        {showHistory && (
          <div className="mt-3 space-y-4" data-testid="history-content">
            <HistoryBucket title={lang === "es" ? "Aceptadas" : "Accepted"} rows={history.accepted} kind="accepted" lang={lang} />
            <HistoryBucket title={lang === "es" ? "Pendientes" : "Pending"} rows={history.pending} kind="pending" lang={lang} onResend={resend} resendingId={resendingId} />
            <HistoryBucket title={lang === "es" ? "Vencidas" : "Expired"} rows={history.expired} kind="expired" lang={lang} onResend={resend} resendingId={resendingId} />
          </div>
        )}
      </div>

      <div className="card border-l-[3px] border-l-teal text-[13px] text-teal-deep flex items-start gap-2 bg-teal-soft/50">
        <IconLock size={16} className="mt-0.5" />
        <span>
          {lang === "es"
            ? "El expediente no se comparte con la institución sin tu autorización. La decisión es humana."
            : "The case file is not shared with the institution without your authorization."}
        </span>
      </div>
    </div>
  );
}

function HistoryBucket({ title, rows, kind, lang, onResend, resendingId }) {
  const badge = kind === "accepted" ? "bg-okbg text-oktext" : kind === "pending" ? "bg-warnbg text-warntext" : "bg-errbg text-errtext";
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(lang === "es" ? "es-HN" : "en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";
  return (
    <div>
      <p className="text-[12.5px] font-mono uppercase tracking-wide text-tert mb-2">
        {title} <span className={`tag ml-1 ${badge}`}>{rows.length}</span>
      </p>
      {rows.length === 0 ? (
        <p className="text-tert text-[12px] italic">{lang === "es" ? "Sin registros." : "No records."}</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 flex-wrap p-2 rounded-md hover:bg-line/20" data-testid={`history-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium truncate">
                  {r.name} <span className="text-tert font-normal">· {r.role_label_es}</span>
                </p>
                <p className="text-[11.5px] text-tert font-mono truncate">
                  {r.email || "—"} · {lang === "es" ? "invitado el" : "invited"} {fmt(r.created_at)}
                  {r.accepted_at && ` · ${lang === "es" ? "aceptado el" : "accepted"} ${fmt(r.accepted_at)}`}
                  {r.invite_expires_at && kind !== "accepted" && ` · ${lang === "es" ? "vence" : "expires"} ${fmt(r.invite_expires_at)}`}
                  {r.last_reminded_at && ` · ${lang === "es" ? "último recordatorio" : "last reminded"} ${fmt(r.last_reminded_at)} (${r.reminders_sent || 0})`}
                </p>
              </div>
              {(kind === "pending" || kind === "expired") && onResend && r.email && (
                <button
                  className="btn-ghost btn text-[10.5px] px-2 py-1"
                  onClick={() => onResend(r)}
                  disabled={resendingId === r.id}
                  data-testid={`history-resend-${r.id}`}
                >
                  <IconMailForward size={11} /> {resendingId === r.id ? "…" : (lang === "es" ? "Reenviar" : "Resend")}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Tab: BOTIQUÍN DE CALMA ----------
function TabKit({ child, viewMode }) {
  const { lang } = useI18n();
  const canEdit = viewMode !== "docente";
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [category, setCategory] = useState("regulación");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/circulo/children/${child.id}/kit`);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [child.id]);

  const add = async () => {
    if (!title.trim()) return;
    setSaving(true); setError("");
    try {
      await api.post(`/circulo/children/${child.id}/kit`, { title: title.trim(), detail: detail.trim() || null, category });
      setTitle(""); setDetail("");
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/circulo/children/${child.id}/kit/${id}`);
    await load();
  };

  return (
    <div className="space-y-4">
      <p className="text-muted text-[13.5px] flex items-center gap-2">
        <IconFirstAidKit size={16} className="text-honey" />
        {lang === "es"
          ? `Lo que de verdad ayuda a ${child.name.split(" ")[0]} en un momento difícil, listo para quien esté con él.`
          : "What really helps in a difficult moment."}
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        {items.length === 0 ? (
          <p className="text-tert text-[13px] col-span-2">{lang === "es" ? "Sin herramientas aún." : "No items yet."}</p>
        ) : items.map((k) => (
          <div key={k.id} className="card" data-testid={`kit-item-${k.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <span className="tag">{k.category}</span>
                <h4 className="font-display text-[16px] font-medium mt-2">{k.title}</h4>
              </div>
              {canEdit && (
                <button className="btn-ghost btn text-[11px] px-2 py-1" onClick={() => remove(k.id)} data-testid={`kit-remove-${k.id}`}>
                  <IconTrash size={12} />
                </button>
              )}
            </div>
            {k.detail && <p className="text-[13.5px] mt-2 text-muted leading-relaxed">{k.detail}</p>}
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="card">
          <p className="lbl m-0 mb-3">{lang === "es" ? "Agregar herramienta" : "Add item"}</p>
          <div className="field">
            <label>{lang === "es" ? "Título" : "Title"}</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="kit-title" />
          </div>
          <div className="field">
            <label>{lang === "es" ? "Detalle" : "Detail"}</label>
            <textarea className="textarea" value={detail} onChange={(e) => setDetail(e.target.value)} data-testid="kit-detail" />
          </div>
          <div className="field">
            <label>{lang === "es" ? "Categoría" : "Category"}</label>
            <select className="select-css" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="kit-category">
              <option value="regulación">regulación</option>
              <option value="sensorial">sensorial</option>
              <option value="comunicación">comunicación</option>
              <option value="emergencia">emergencia</option>
            </select>
          </div>
          <button className="btn" onClick={add} disabled={saving} data-testid="kit-add">
            <IconPlus size={13} /> {saving ? "..." : (lang === "es" ? "Agregar" : "Add")}
          </button>
          {error && <p className="text-errtext text-[13px] mt-2">{error}</p>}
        </div>
      )}
    </div>
  );
}
